/**
 * DesktopUpdateBanner
 *
 * Sticky bottom-of-screen toast that appears in the Electron desktop app once
 * `electron-updater` has finished downloading a newer version in the
 * background. Offers "Restart now" (calls into main → `quitAndInstall`) and
 * "Later" (just hides the banner — `autoInstallOnAppQuit` in main.ts means
 * the update still installs the next time the user quits, so dismissing here
 * is non-destructive).
 *
 * Renders nothing outside Electron, so it's safe to mount unconditionally
 * from DesktopLayout (which is itself web/electron-only in practice). The
 * subscription is owned here rather than in DesktopLayout so the parent
 * doesn't need to thread visibility state around — drop the component in,
 * forget about it.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { getElectronBridge, isElectron, isWeb, DESKTOP_BREAKPOINT } from '../utilities/platform';
import Icon from './Icon';

// Banner geometry on desktop. The bottom anchor is set inside the widget's
// padding (gutter + 8) so the banner sits inside the rounded card, not in
// the backdrop gutter outside it. Width is capped tight — banners carry one
// short message + two buttons and stretching them across the full widget
// body produces a system-bar feel that dwarfs the content. 520px matches
// the toast cap used by the global Notification and roughly mirrors macOS /
// Slack / Discord native notifications.
const WIDGET_GUTTER = 64;
const DESKTOP_TOAST_MAX_WIDTH = 520;

export default function DesktopUpdateBanner() {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isElectron) return;
    const bridge = getElectronBridge();
    if (!bridge?.onUpdateAvailable) return;
    return bridge.onUpdateAvailable(() => setVisible(true));
  }, []);

  if (!visible || !isElectron) return null;

  const isDesktopWidth = isWeb && width >= DESKTOP_BREAKPOINT;

  const handleRestart = () => {
    const bridge = getElectronBridge();
    bridge?.installUpdate();
  };

  const handleLater = () => setVisible(false);

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.card,
          borderColor: theme.colors.border,
          bottom: isDesktopWidth ? WIDGET_GUTTER + 8 : 16,
        },
        isDesktopWidth
          ? {
              // Centered toast on desktop: cap at toast width or viewport-32
              // (whichever is smaller — narrow windows still get sane gutters).
              left: 0,
              right: 0,
              marginLeft: 'auto' as any,
              marginRight: 'auto' as any,
              width: Math.min(DESKTOP_TOAST_MAX_WIDTH, width - 32),
              maxWidth: DESKTOP_TOAST_MAX_WIDTH,
            }
          : { left: 16, right: 16 },
      ]}
    >
      <View style={[styles.iconWrap, { backgroundColor: theme.colors.primary + '18' }]}>
        <Icon icon={['fas', 'arrow-up']} size={14} color={theme.colors.primary} />
      </View>
      <View style={styles.body}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Update ready</Text>
        <Text style={[styles.message, { color: theme.colors.muted }]}>
          A new version of Eleplan has been downloaded. Restart to apply.
        </Text>
      </View>
      <TouchableOpacity onPress={handleLater} style={styles.laterBtn} activeOpacity={0.7}>
        <Text style={[styles.laterText, { color: theme.colors.muted }]}>Later</Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={handleRestart}
        style={[styles.restartBtn, { backgroundColor: theme.colors.primary }]}
        activeOpacity={0.85}
      >
        <Text style={[styles.restartText, { color: theme.colors.primaryText || '#fff' }]}>Restart</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    zIndex: 1000,
    ...Platform.select({
      web: {
        boxShadow: '0 12px 32px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.10)',
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.18,
        shadowRadius: 14,
        elevation: 8,
      },
    }),
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  body: { flex: 1, minWidth: 0 },
  title: { fontSize: 14, fontWeight: '700', marginBottom: 1 },
  message: { fontSize: 12, lineHeight: 16 },
  laterBtn: { paddingHorizontal: 12, paddingVertical: 8, marginLeft: 8 },
  laterText: { fontSize: 13, fontWeight: '600' },
  restartBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, marginLeft: 4 },
  restartText: { fontSize: 13, fontWeight: '700' },
});
